import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import type { QuickBooksTokens } from "@/lib/types/quickbooks-unified";

export const useSyncTimeEntryToQuickBooks = () => {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async ({
      timeEntryId,
      tokens,
      realmId,
    }: {
      timeEntryId: string;
      tokens: QuickBooksTokens;
      realmId: string;
    }) => {
      const response = await fetch("/api/quickbooks/time-activities", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          tokens,
          realmId,
          timeEntryId,
          action: "sync",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to sync time entry");
      }

      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
    },
  });
};

export const useBulkSyncTimeEntries = () => {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async ({
      timeEntryIds,
      tokens,
      realmId,
    }: {
      timeEntryIds: string[];
      tokens: QuickBooksTokens;
      realmId: string;
    }) => {
      // `/api/quickbooks/time-activities` dispatches on the `action` this hook
      // already sends. The dedicated `/bulk-sync` route it used to call was a
      // second, untested copy of the same loop — and it wrapped every response
      // (including its own 400s) in `withAuth`'s `{ data: … }` envelope with a
      // fresh HTTP 200, so nothing here could ever see a failure.
      const response = await fetch("/api/quickbooks/time-activities", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          tokens,
          realmId,
          timeEntryIds,
          action: "bulk-sync",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to bulk sync time entries");
      }

      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
    },
  });
};
